import { zodResolver } from '@hookform/resolvers/zod';
import { COUNTRY_ECOTAX_TYPE } from '@wyz/types';
import { Button, Card, Col, FormContainer, notify, Row, Title } from '@wyz/ui';
import { invariant } from '@wyz/utils';
import { isEmpty } from 'lodash-es';
import { BaseSyntheticEvent, useMemo, useState } from 'react';
import { useForm, useWatch } from 'react-hook-form';
import { useNavigate, useParams } from 'react-router';
import { PdfModal, SelectField, TextField } from '~/common/components';
import { Trans, useConfig, useI18n } from '~/common/context';
import {
  useManageQuoteCart,
  useManageQuotes,
  useManageQuoteSetting,
  useManageQuoteServicePricingPolicies,
} from '~/purchase/api';
import { SERVICE_TYPE } from '~/purchase/schemas';
import { GeometryServiceRow } from '~/purchase/components';
import {
  QuoteContactForm,
  QuoteProductsForm,
  QuoteServicesForm,
  QuoteTotalSection,
} from '~/purchase/components';
import { QuoteCartProvider, usePurchaseContext } from '~/purchase/context';
import { QuoteHelpers } from '~/purchase/helpers';
import { generateQuoteSettingSchema } from '~/purchase/schemas';
import {
  IQuoteCart,
  IQuoteCartAction,
  IQuoteCartAddServicePayload,
  IQuoteCartContext,
  IQuoteCartEdit,
  IQuoteCartForm,
  IQuoteCartStep,
  IQuoteEditPayload,
  IQuoteSetting,
  IRimType,
} from '~/purchase/types';

type Props = {
  defaultValues: IQuoteCartEdit;
  setting: IQuoteSetting;
  onNext: () => void;
  step: IQuoteCartStep;
};

const SAVE_AND_SEND_BUTTON_ID = 'save-and-send';
const PRINT_BUTTON_ID = 'print';

function EditQuoteForm({ defaultValues, setting, step, onNext }: Props) {
  const { t } = useI18n();
  const { state } = usePurchaseContext();
  const targetCustomer = state.targetCustomer;
  invariant(targetCustomer);
  const { id: quoteReference } = useParams<{ id: string }>();
  const { apiUrl, tenantCode } = useConfig();
  invariant(quoteReference);
  const navigate = useNavigate();
  const quoteSettingManagement = useManageQuoteSetting({
    get: { enabled: true },
  });
  const quoteManagement = useManageQuotes({
    edit: {
      enabled: true,
      onSuccess: () => {
        notify.success(<Trans i18nKey='app.common.messages.success' />, {
          id: 'toast-success-quote-edit',
        });
        void navigate('/quotes/history');
      },
      onError: (error) => {
        notify.error(error.message, { id: 'toast-error-quote-edit' });
      },
    },
  });
  const [pdf, setPdf] = useState<string | null>(null);

  const methods = useForm<IQuoteCartForm>({
    resolver: zodResolver(
      generateQuoteSettingSchema(quoteSettingManagement.get.data?.data),
    ),
    defaultValues: {
      products: defaultValues.cart.items.reduce(
        (products, item) => ({
          ...products,
          [item.id]: { quantity: item.quantity },
        }),
        {},
      ),
      rimType: defaultValues.cart.rimType,
      comment: t(
        'app.purchase.quote_settings.form.defaults.additional_message',
      ),
      validityDays: setting.validityDays,
      contact: defaultValues.contact,
    },
  });

  const [watchedContactMail, watchedGdprAccepted] = useWatch({
    name: ['contact.email', 'contact.gdprAccepted'],
    control: methods.control,
  });
  const allowSendMail = watchedGdprAccepted && !isEmpty(watchedContactMail);

  const [cart, setCart] = useState<IQuoteCart>({
    ...defaultValues.cart,
  });

  const manageServicePricingPolicies = useManageQuoteServicePricingPolicies({
    list: {
      enabled: step === 'SERVICES',
      args: {
        serviceTypes: [SERVICE_TYPE.GEOMETRY],
        tyresMetadata: cart.items.map((item) => ({
          vehicleTypePublicKey: item.tyre.vehicleType,
        })),
      },
    },
  });

  const manageCart = useManageQuoteCart({
    refreshMountingServices: {
      enabled: true,
      onSuccess: (data) => setCart({ ...data.data }),
    },
  });

  const actions: IQuoteCartAction = {
    updatePrice: {
      process: (
        data: {
          items?: Array<{ id: string; price: number }>;
          services?: Array<{ id: string; price: number }>;
        },
        onSuccess?: () => void,
      ) => {
        setCart({
          ...QuoteHelpers.updatePrice(cart, data),
        });
        onSuccess?.();
      },
      isLoading: false,
    },
    addService: {
      process: (data: IQuoteCartAddServicePayload, onSuccess?: () => void) => {
        setCart({
          ...QuoteHelpers.addServiceToCart(cart, data),
        });
        onSuccess?.();
      },
      isLoading: false,
    },
    removeService: {
      process: (id: string) => {
        setCart({
          ...QuoteHelpers.removeServiceFromCart(cart, { id: id }),
        });
      },
      isLoading: false,
    },
    removeItem: {
      process: (id: string) => {
        const updatedCart = QuoteHelpers.removeItemFromCart(cart, id);
        setCart({
          ...updatedCart,
        });
        manageCart.refreshMountingServices.mutate({
          body: { ...updatedCart },
          reference: quoteReference,
        });
      },
      isLoading: false,
    },
    updateRimType: {
      process: (data: { rimType: IRimType }) => {
        const updatedCart = {
          ...cart,
          rimType: data.rimType,
        };
        setCart({
          ...updatedCart,
        });
        manageCart.refreshMountingServices.mutate({
          body: { ...updatedCart },
          reference: quoteReference,
        });
      },
      isLoading: false,
    },
    updateItemQuantity: {
      process: (
        id: string,
        body: { quantity: number },
        onSuccess?: () => void,
      ) => {
        const updatedCart = QuoteHelpers.updateItemQuantity(cart, id, body);
        setCart({ ...updatedCart });
        manageCart.refreshMountingServices.mutate({
          body: { ...updatedCart },
          reference: quoteReference,
        });

        onSuccess?.();
      },
      isLoading: false,
    },
    addItem: {
      process: () => {},
      isLoading: false,
    },
    reset: {
      process: () => {},
      isLoading: false,
    },
    submit: {
      process: ({ customerId, payload, print, sendMail }) => {
        quoteManagement.edit.mutate(
          {
            customerId: customerId,
            reference: quoteReference,
            body: { ...(payload as IQuoteEditPayload), sendEmail: sendMail },
          },
          {
            onSuccess: () => {
              if (print) {
                setPdf(
                  `${apiUrl}/offers/api/tenants/${tenantCode}/customers/${customerId}/quotes/${quoteReference}/print`,
                );
              }
            },
          },
        );
      },
      isLoading: quoteManagement.edit.isLoading,
    },
  };

  const data: IQuoteCartContext['data'] = useMemo(() => {
    return {
      services: cart.services,
      vehicleTypes: cart.items.map((item) => item.tyre.vehicleType),
      defaultProducts: cart.items.reduce(
        (products, item) => ({
          ...products,
          [item.id]: { quantity: item.quantity },
        }),
        {},
      ),
      defaultRimType: cart.rimType,
      vatPercentage: cart.vatPercentage,
      isCartEmpty: cart.items.length === 0,
      isSuccess: true,
      isProcessing: false,
      isLoading: false,
      currency: undefined,
      totalDiscount: 0,
      totalTaxExcl: 0,
      totalTaxIncl: 0,
      totalVatPrice: 0,
    };
  }, [cart.items, cart.rimType, cart.services, cart.vatPercentage]);

  return (
    <FormContainer
      methods={methods}
      submitHandler={(data, event) => {
        const buttonId =
          (
            event as BaseSyntheticEvent<SubmitEvent> | undefined
          )?.nativeEvent.submitter?.getAttribute('id') ?? undefined;

        const payload: IQuoteEditPayload = {
          items: cart.items.map((item) => ({
            ...item,
          })),
          services: cart.services.map((service) => ({
            ...service,
          })),
          contact: data.contact,
          comment: data.comment,
          validityDays: data.validityDays,
          rimType: cart.rimType,
        };

        if (buttonId === PRINT_BUTTON_ID) {
          actions.submit.process({
            payload,
            sendMail: false,
            print: true,
            customerId: targetCustomer.id,
          });
          return;
        }

        if (buttonId === SAVE_AND_SEND_BUTTON_ID) {
          actions.submit.process({
            payload,
            sendMail: true,
            print: false,
            customerId: targetCustomer.id,
          });
          return;
        }
        actions.submit.process({
          payload,
          sendMail: false,
          print: false,
          customerId: targetCustomer.id,
        });
      }}
    >
      <Row className='mb-3'>
        <Title heading='lg' weight='bold'>
          <Trans
            i18nKey='app.purchase.quote_edit_page.title'
            values={{
              reference: quoteReference,
            }}
          />
        </Title>
      </Row>
      <QuoteCartProvider mode='EDIT_QUOTE' actions={actions} data={data}>
        <Row>
          <Col lg={8} sm={12}>
            {step === 'PRODUCTS' && (
              <QuoteProductsForm
                cart={cart}
                actions={actions}
                searchEnabled={false}
                customer={targetCustomer}
                settings={setting}
              />
            )}
            {step === 'SERVICES' && (
              <QuoteServicesForm>
                <GeometryServiceRow
                  manageServicePricingPolicies={manageServicePricingPolicies}
                />
              </QuoteServicesForm>
            )}
            {step === 'CONTACT' && <QuoteContactForm settings={setting} />}
          </Col>
          <Col lg={4} sm={12}>
            {step !== 'CONTACT' ? (
              <QuoteTotalSection
                cart={{
                  ...cart,
                  groups: cart.groups.map((group) => ({
                    ...group,
                    totalTaxExcl: QuoteHelpers.calculateTotalTaxExcl(group),
                    totalTaxIncl: QuoteHelpers.calculateTotalTaxIncl(group),
                  })),
                }}
                onNext={onNext}
                actions={actions}
                privileges={{
                  ecotaxType: COUNTRY_ECOTAX_TYPE.NOT_DISPLAYED,
                }}
              />
            ) : (
              <Card>
                <Card.Body>
                  <Title
                    className='mb-3'
                    heading='lg'
                    weight='bold'
                    color='secondary'
                  >
                    <Trans i18nKey='app.purchase.quote_cart_page.send' />
                  </Title>
                  <SelectField
                    name='validityDays'
                    label={
                      <Trans i18nKey='app.purchase.quote_cart_page.form.validity' />
                    }
                    options={Array(30)
                      .fill(undefined)
                      .map((_, i) => ({
                        label: t(
                          'app.purchase.quote_cart_page.form.validity_day',
                          { count: i + 1 },
                        ),
                        value: i + 1,
                      }))}
                    required
                  />
                  <TextField
                    label={<Trans i18nKey='app.common.comment' />}
                    name='comment'
                    type='text'
                    as='textarea'
                    placeholder='Laissez un commentaire au destinataire du devis...'
                    rows={6}
                  />
                  <div className='space-y-4 mt-3'>
                    <Button
                      id={SAVE_AND_SEND_BUTTON_ID}
                      variant='primary'
                      type='submit'
                      fullWidth
                      disabled={!allowSendMail}
                      isLoading={quoteManagement.edit.isLoading}
                    >
                      <Trans i18nKey='app.purchase.quote_cart_page.form.save_and_send' />
                    </Button>
                    <Button
                      id='save'
                      variant='tertiary-blue'
                      type='submit'
                      fullWidth
                      isLoading={quoteManagement.edit.isLoading}
                    >
                      <Trans i18nKey='app.purchase.quote_cart_page.form.save' />
                    </Button>
                    <Button
                      id={PRINT_BUTTON_ID}
                      variant='tertiary-blue'
                      type='submit'
                      fullWidth
                      isLoading={quoteManagement.edit.isLoading}
                    >
                      <Trans i18nKey='app.purchase.quote_cart_page.form.print' />
                    </Button>
                  </div>
                </Card.Body>
              </Card>
            )}
          </Col>
        </Row>
      </QuoteCartProvider>
      {pdf !== null && <PdfModal url={pdf} onClose={() => setPdf(null)} />}
    </FormContainer>
  );
}

export default EditQuoteForm;
